/**
 * Reference Code Mapping for Retaining Walls
 * Maps field names to Evaluate codes (resourceCode in Item Library API)
 * These are the detailed build-up items for retaining walls
 */

export interface RetainingWallsReferenceCodeMapping {
  code?: string;
  /** For fields whose code depends on a runtime toggle (e.g. Screen Walls &
   * EO Pillar's Free Issue / M&J Supply Configure selection) instead of a
   * fixed code. Resolved via a `dynamicCodeResolver` at the consumer. */
  dimensionValueKey?: string;
}

/**
 * Retaining Walls Reference Codes
 * Maps field names from retaining walls build-up items to Evaluate codes
 */
export const RETAINING_WALLS_REFERENCES: Record<
  string,
  RetainingWallsReferenceCodeMapping
> = {
  // Foundation Works
  excavateTrenchAndTipOnsite: { code: "GWALPGW0026" },
  compactFormation: { code: "GWALPGW0027" },
  concreteBlinding: { code: "GWALPGW0015" },

  // Mesh Reinforcement (Horizontal)
  a393Mesh: { code: "GWALPRM004" },
  a252Mesh: { code: "GWALPRM003" },
  b503Mesh: { code: "GWALPRM006" },
  b785Mesh: { code: "GWALPRM007" },

  // Concrete & Masonry
  concreteToFoundation: { code: "GWALPGW0013" },
  blockwork100: { code: "GWALPS007" },
  facingBrickworkFreeIssue: { code: "GWALPGW0021" },
  facingBrickworkMJSupply: { code: "GWALPGW0020" },

  // BoE Coping — same base code as the walls build-up's `boe_coping_*_wide`
  // rows (see `WALLS_BUILD_UP_ITEM_CODE_MAP` in mock/walls-section-build-up.ts).
  // Was previously "ACL012A" (that's actually Screen Walls' "Log 1200mm" code,
  // `screenWallsLog` below) — the mismatch meant the historical BOQ snapshot
  // (`groundworksItemLibrary.retainingWalls`, built from this map) never
  // captured GWALPGW008's rate, so BoE coping priced at 0 whenever a saved
  // version was reopened, even though the live/current version priced it
  // correctly off the item library.
  boeCopiing1brWide: { code: "GWALPGW008" },
  boeCopiing15brWide: { code: "GWALPGW008" },
  boeCopiing20brWide: { code: "GWALPGW008" },
  boeCopiing25brWide: { code: "GWALPGW008" },

  // Backfill & Drainage
  asDugBackfill: { code: "GWALP034" },
  stockpileOnsite: { code: "GWALP033" },
  perforatedPipe150mm: { code: "GWALPGW0024" },
  concreteHaunching: { code: "GWALPGW0023" },
  granularBackfill: { code: "GWALPGW0032" },

  // Waterproofing
  waterproofingRIW1Coat: { code: "GWALPGW0028" },
  aquasealMemb: { code: "GWALPGW0029" },
  waterproofingRIW2CoatProtectionBoard: { code: "GWALPGW0030" },
  weepholes75dia: { code: "GWALPGW0016" },
  terram: { code: "ALP031" },

  // Fence Posts
  fencePostAt2400mmCcs: { code: "GWALPGW0022" },

  // Geo Wall Installation
  installGeoWalls: { code: "GWALPGW0037" },
  purchaseInclCarriageWasteFP: { code: "ZEV23" },

  // Tobermore Wall Items
  excavationOfEmbankment: { code: "GWALPGW0026" },
  tobermoreStockpileOnsite: { code: "ALP033A" },
  batterToFaceOfEmbankment: { code: "GWALPGW0027" },
  excavationOfBase: { code: "GWALPGW0026" },
  levelAndCompactFormation: { code: "GWALPGW0027" },
  concreteToBase: { code: "GWALPGW0013" },
  tobermoreSupplyGrandSecura: { code: "SCAANC007" },
  tobermoreSecuraInstallMJ: { code: "GWALPGW0037" },
  tobermoreSupplyGeogrid: { code: "SCAANC007" },
  mjInstallationOfGeogrid: { code: "ALP032B" },
  freeDrainingGranularBackfill300wColumn: { code: "GWALPGW0032" },
  granularBackfillToRearOfWall: { code: "GWALPGW0032" },
  landDrainToRearOfWall: { code: "GWALPGW0024" },
  allowSiltTrap: { code: "GWALPDD019" },
  allowToConnectLandDrain: { code: "GWALPDD057" },

  // Rebar & Vertical Mesh
  rebar: { code: "GWALPGW0019" },
  a393MeshVertical: { code: "GWALPGW0033" },
  a252MeshVertical: { code: "GWALPGW0034" },
  b503MeshVertical: { code: "GWALPGW0035" },
  b785MeshVertical: { code: "GWALPGW0036" },

  // Concrete Stem & Additional Works
  concreteStem: { code: "GWALPGW0014" },
  additionalExcavation: { code: "GWALPGW0026" },
  additionalConcrete: { code: "GWALPGW0015" },

  // Screen Walls
  screenWallFoundationFreeIssue: { code: "GWALPGW003" },
  screenWallEOPillersFreeIssue: { code: "GWALPGW003A" },
  screenWallFoundationMJSupply: { code: "GWALPGW003B" },
  screenWallEOPillersMJSupply: { code: "GWALPGW003C" },
  slabOnEdge: { code: "ACL012A" }, // % to give L&P value
  logWallNe1200mm: {}, // No code provided
  gravelBoard: { code: "GWALPGW0023" },
  screenWallSof: { code: "GWALPGW0043" },
  screenWallLog: { code: "GWALPGW0044" },

  // High-level retaining wall fields (from retainingWallsFieldMapping)
  screenWalls: { dimensionValueKey: "screenWalls" },        // Screen Walls (m) — code depends on Free Issue/M&J Supply Configure toggle
  screenWallsEO: { dimensionValueKey: "screenWallsEO" },    // EO Pillar (Nr) — code depends on Free Issue/M&J Supply Configure toggle
  screenWallsSof: { code: "GWALPGW0043" },    // SoE 450mm (m)
  screenWallsLog: { code: "ACL012A" },        // Log 1200mm (m)
  screenWallsGravelBoard: { code: "GWALPGW0023" }, // Gravel board (m)
  screenWallsAdditionalExcavation: { code: "GWALPGW0026" }, // Additional Excavation (m3)
  screenWallsAdditionalConcrete: { code: "GWALPGW0015" }, // Additional Concrete (m3)
  grw450: {},
  grw600: {},
  grw750: {},
  grw900: {},
  grw1050: {},
  grw1200: {},
  grw1500: {},
  grw1800: {},
  grw2000: {},
  ccrw1500: {},
  ccrw1800: {},
  ccrw2000: {},
  ccrw2250: {},
  ccrw2500: {},
  ccrw2750: {},
  ccrw3000: {},
  ccrw3250: {},
  ccrw3500: {},
  drw450: {},
  drw600: {},
  drw750: {},
  drw900: {},
  drw1050: {},
  drw1200: {},
  drw1500: {},
  drw1800: {},
  drw2000: {},
  drw2250: {},
  drw2500: {},
  drw2750: {},
  drw3000: {},
};

/**
 * Get reference code for a specific field
 */
export const getRetainingWallsReferenceCode = (
  referenceKey: string,
): string | undefined => {
  return RETAINING_WALLS_REFERENCES[referenceKey]?.code;
};

/**
 * Get full reference information for a specific field
 */
export const getRetainingWallsReference = (
  referenceKey: string,
): RetainingWallsReferenceCodeMapping | undefined => {
  return RETAINING_WALLS_REFERENCES[referenceKey];
};

/**
 * Standalone Screen Walls line items rendered as the first section of the
 * Ret & Screen Walls Bill Summary. These five fields come from the Retaining
 * Walls take-off table (not the build-up specs) — they're priced as
 * `qty × library rate` independently of any wall-spec build-up. Shared by
 * `RetScreenWallsBillSummary.tsx`, `groundworksBoqSummaryComputer.ts`
 * (`computeRetainingWallsSection`) and `summaryCalculations.ts`
 * (`calculateRetScreenWallsGrandTotal`) so the on-screen Bill Summary, the
 * BoQ JSON payload, and every grand-total roll-up agree to the penny.
 */
export interface ScreenWallBoqField {
  /** Field name on `retainingWallsTable` rows. */
  id: string;
  /** Description rendered in the BillSummary / BoQ. */
  label: string;
  /** Unit shown alongside the quantity. */
  unit: 'm' | 'm³' | 'Nr';
  /** Item-library `resourceCode` for rate lookup. */
  code: string;
}

export const SCREEN_WALL_BOQ_FIELDS: readonly ScreenWallBoqField[] = [
  { id: 'screenWalls',            label: 'Screen Walls',      unit: 'm',  code: 'GWALPGW003'  },
  { id: 'screenWallsEO',          label: 'EO Pillar',         unit: 'Nr', code: 'GWALPGW003A' },
  { id: 'screenWallsSof',         label: 'SoE 450mm',         unit: 'm',  code: 'GWALPGW0043' },
  { id: 'screenWallsLog',         label: 'Log 1200mm',        unit: 'm',  code: 'ACL012A'     },
  { id: 'screenWallsGravelBoard', label: 'Gravel board',      unit: 'm',  code: 'GWALPGW0023'  },
  { id: 'screenWallsAdditionalExcavation', label: 'Additional Excavation', unit: 'm³', code: 'GWALPGW0026' },
  { id: 'screenWallsAdditionalConcrete',   label: 'Additional Concrete',   unit: 'm³', code: 'GWALPGW0015' },
] as const;

// ─── Screen Walls / EO Pillar supply-type code resolvers ───────────────────
// The Configure modal on the Retaining Walls page's "Ret & Screen Take off"
// tab has two INDEPENDENT dropdowns — one for Screen Walls, one for EO
// Pillar — each picking Free Issue or M&J Supply on its own. The choices are
// stored as two separate label strings on
// `tender.groundworks.wallsBuildUpConfig`:
//   - `screenWallsLabel`: 'Screen Walls - Free Issue' → GWALPGW003   | 'Screen Walls - M&J Supply' → GWALPGW003B
//   - `eoPillarLabel`:    'EO Pillar - Free Issue'    → GWALPGW003A  | 'EO Pillar - M&J Supply'    → GWALPGW003C
// `getScreenWallFieldCode` resolves the code to use for a `SCREEN_WALL_BOQ_FIELDS`
// entry, overriding the static `code` for `screenWalls`/`screenWallsEO` only —
// every other field (SoE 450mm, Log 1200mm, Gravel board, etc.) is unaffected.
export const SCREEN_WALLS_FREE_ISSUE_LABEL = 'Screen Walls - Free Issue';
export const SCREEN_WALLS_M_J_SUPPLY_LABEL = 'Screen Walls - M&J Supply';
export const EO_PILLAR_FREE_ISSUE_LABEL = 'EO Pillar - Free Issue';
export const EO_PILLAR_M_J_SUPPLY_LABEL = 'EO Pillar - M&J Supply';

export function resolveScreenWallsCode(screenWallsLabel?: string): string {
  return screenWallsLabel === SCREEN_WALLS_M_J_SUPPLY_LABEL ? 'GWALPGW003B' : 'GWALPGW003';
}

export function resolveEOPillarCode(eoPillarLabel?: string): string {
  return eoPillarLabel === EO_PILLAR_M_J_SUPPLY_LABEL ? 'GWALPGW003C' : 'GWALPGW003A';
}

export function getScreenWallFieldCode(
  field: ScreenWallBoqField,
  screenWallsLabel?: string,
  eoPillarLabel?: string,
): string {
  if (field.id === 'screenWalls') return resolveScreenWallsCode(screenWallsLabel);
  if (field.id === 'screenWallsEO') return resolveEOPillarCode(eoPillarLabel);
  return field.code;
}

/**
 * Bill Summary row description for a `SCREEN_WALL_BOQ_FIELDS` entry —
 * appends " - Free Issue"/" - M&J Supply" to `screenWalls`/`screenWallsEO`'s
 * label (matching the take-off table's column titles), every other field's
 * label is unaffected.
 */
export function getScreenWallFieldLabel(
  field: ScreenWallBoqField,
  screenWallsLabel?: string,
  eoPillarLabel?: string,
): string {
  if (field.id === 'screenWalls') {
    const suffix = screenWallsLabel === SCREEN_WALLS_M_J_SUPPLY_LABEL ? 'M&J Supply' : 'Free Issue';
    return `${field.label} - ${suffix}`;
  }
  if (field.id === 'screenWallsEO') {
    const suffix = eoPillarLabel === EO_PILLAR_M_J_SUPPLY_LABEL ? 'M&J Supply' : 'Free Issue';
    return `${field.label} - ${suffix}`;
  }
  return field.label;
}
